import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { useCrmSettings, useLeadSources, useM3Mutation, useReasons } from '@/api/m3';
import type { LeadSource, ReasonKind, RefusalReason } from '@/api/m3-types';
import { RowActions } from '@/components/kit/ActionMenu';
import { Button } from '@/components/kit/Button';
import { Switch, TextLink } from '@/components/kit/Controls';
import { TextField } from '@/components/kit/Field';
import { Modal } from '@/components/kit/Modal';
import { ErrorState, Note, Skeleton } from '@/components/kit/Note';
import { Panel, Stack } from '@/components/kit/Panel';
import { useToast } from '@/components/kit/Toast';
import { errorText } from '@/features/base/shared/useUndoable';
import { CrmHeader } from './CrmHeader';
import { useM3Submit } from './useM3Submit';

const REASON_LISTS: { kind: ReasonKind; title: string; note: string }[] = [
  { kind: 'lead', title: 'Причины отказа заявки', note: 'Обязательны при отказе заявки. Использованные не удаляются — только выключаются.' },
  { kind: 'deal', title: 'Причины проигрыша сделки', note: 'Обязательны при отметке «Проиграна». Из них строится отчёт «Потери».' },
  { kind: 'registration', title: 'Причины снятия регистрации', note: 'Обязательны при отказе от номера.' },
  { kind: 'participant', title: 'Причины снятия участника', note: 'Обязательны, когда участника снимают с номера.' },
];

type Dialog =
  | { kind: 'rename'; source: LeadSource }
  | { kind: 'reissue'; source: LeadSource }
  | { kind: 'vk'; source: LeadSource }
  | { kind: 'reason'; appliesTo: ReasonKind }
  | { kind: 'renameReason'; reason: RefusalReason };

/**
 * Настройки CRM (viewSet в макете, ТЗ 5.7): каналы заявок с кодом
 * приёма, пороги, причины. Удаления нет — только выключение: канал
 * и причина прошлых записей остаются в отчётах.
 */
export function CrmSettingsPage() {
  const sources = useLeadSources();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const close = () => setDialog(null);

  return (
    <div className="main">
      <CrmHeader current="/crm/settings" />
      <div className="two">
        <Stack>
          <Panel title="Каналы заявок" count={sources.data?.length}>
            {sources.error ? (
              <ErrorState message={sources.error.message} onRetry={() => void sources.refetch()} />
            ) : sources.isLoading ? (
              <Skeleton width="70%" />
            ) : (
              (sources.data ?? []).map((s) => <SourceRow key={s.id} source={s} onDialog={setDialog} />)
            )}
            <AddSource />
            <Note>
              Код приёма — пароль канала для приёма «POST /order/external»: с ним сайт и рекламные формы заводят заявки.
              Видит и перевыпускает его руководитель продаж. Формы сбора заявок ВКонтакте подключаются к каналу ссылкой
              «ВКонтакте…». Удаления нет — только скрытие: канал прошлых заявок остаётся в отчётах. Справочник тот же, что
              в CRM студий ANiceDay.
            </Note>
          </Panel>
          <Thresholds />
        </Stack>
        <Stack>
          {REASON_LISTS.map((l) => (
            <ReasonPanel key={l.kind} {...l} onDialog={setDialog} />
          ))}
        </Stack>
      </div>

      {dialog?.kind === 'rename' ? <RenameSourceModal source={dialog.source} onClose={close} /> : null}
      {dialog?.kind === 'reissue' ? <ReissueModal source={dialog.source} onClose={close} /> : null}
      {dialog?.kind === 'vk' ? <VkModal source={dialog.source} onClose={close} /> : null}
      {dialog?.kind === 'reason' ? <ReasonModal appliesTo={dialog.appliesTo} onClose={close} /> : null}
      {dialog?.kind === 'renameReason' ? <ReasonModal reason={dialog.reason} onClose={close} /> : null}
    </div>
  );
}

function SourceRow({ source: s, onDialog }: { source: LeadSource; onDialog: (d: Dialog) => void }) {
  const toast = useToast();
  const [token, setToken] = useState<string | null>(null);
  const toggle = useM3Mutation(() => api.patch(`/lead-sources/${s.id}`, { isActive: !s.isActive }));
  const reveal = async () => {
    try {
      setToken((await api.get<{ token: string }>(`/lead-sources/${s.id}/token`)).token);
    } catch (e) {
      toast.showError(errorText(e));
    }
  };
  return (
    <div className="chrow">
      <Switch
        checked={s.isActive}
        label=""
        onToggle={() =>
          toggle.mutate(undefined, {
            onSuccess: () => toast.show(s.isActive ? 'Канал скрыт' : 'Канал включён'),
            onError: (e) => toast.showError(errorText(e)),
          })
        }
      />
      <div className="x">
        <div className="t1">
          <span className="n">{s.name}</span>
          <span className="cd">{s.code}</span>
        </div>
        {s.vkGroupId ? (
          <div className="t2">
            ВКонтакте: сообщество <span className="num">{s.vkGroupId}</span>
          </div>
        ) : null}
        <div className="t2">
          код приёма <span className="mono">{token ?? `•••• ${s.tokenTail}`}</span>
          {token ? (
            <TextLink
              onClick={() => {
                void navigator.clipboard?.writeText(token);
                toast.show('Код скопирован');
              }}
            >
              скопировать
            </TextLink>
          ) : (
            <TextLink onClick={() => void reveal()}>показать</TextLink>
          )}
          <TextLink onClick={() => onDialog({ kind: 'reissue', source: s })}>перевыпустить…</TextLink>
          <TextLink onClick={() => onDialog({ kind: 'vk', source: s })}>ВКонтакте…</TextLink>
          <TextLink onClick={() => onDialog({ kind: 'rename', source: s })}>переименовать…</TextLink>
        </div>
      </div>
    </div>
  );
}

function AddSource() {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const { submit, pending } = useM3Submit(
    () => api.post('/lead-sources', { name: name.trim(), code: code.trim().toLowerCase() }),
    'Канал добавлен',
    () => {
      setName('');
      setCode('');
    },
  );
  return (
    <div className="row chadd">
      <div className="col">
        <TextField label="Название" placeholder="Авито" value={name} onValue={setName} />
      </div>
      <div className="col">
        <TextField label="Системный код, латиницей" placeholder="avito" value={code} onValue={setCode} />
      </div>
      <div className="col fix">
        <Button onClick={submit} disabled={!name.trim() || !code.trim() || pending}>
          Добавить
        </Button>
      </div>
    </div>
  );
}

function Thresholds() {
  const settings = useCrmSettings();
  const [idle, setIdle] = useState('');
  const [touch, setTouch] = useState('');
  const [dedup, setDedup] = useState('');
  useEffect(() => {
    if (!settings.data) return;
    setIdle(String(settings.data.idleDays));
    setTouch(String(settings.data.touchMinutes));
    setDedup(String(settings.data.dedupMinutes));
  }, [settings.data]);
  const num = (v: string) => (v.trim() ? Number(v.replace(/\D/g, '')) : null);
  const { submit, pending } = useM3Submit(
    () => api.patch('/crm/settings', { idleDays: num(idle), touchMinutes: num(touch), dedupMinutes: num(dedup) }),
    'Пороги сохранены',
    () => undefined,
  );
  return (
    <Panel title="Пороги">
      {settings.error ? (
        <ErrorState message={settings.error.message} onRetry={() => void settings.refetch()} />
      ) : (
        <>
          <div className="row keep">
            <div className="col">
              <TextField label="Без движения, дней" inputMode="numeric" value={idle} onValue={setIdle} />
            </div>
            <div className="col">
              <TextField label="Время до касания, минут" inputMode="numeric" value={touch} onValue={setTouch} />
            </div>
            <div className="col">
              <TextField label="Защита от повтора, минут" inputMode="numeric" value={dedup} onValue={setDedup} />
            </div>
          </div>
          <Note>
            <b>Без движения</b> — с какого дня сделка без событий подсвечивается. <b>Касание</b> — сколько заявка может
            ждать «Взять в работу». <b>Повтор</b> — тот же номер с того же канала на то же мероприятие в это окно не
            создаёт новую заявку; на другое мероприятие — создаёт.
          </Note>
          <div className="fbtns">
            <Button tone="primary" onClick={submit} disabled={pending || !settings.data}>
              Сохранить пороги
            </Button>
          </div>
        </>
      )}
    </Panel>
  );
}

function ReasonPanel({ kind, title, note, onDialog }: { kind: ReasonKind; title: string; note: string; onDialog: (d: Dialog) => void }) {
  const reasons = useReasons(kind);
  const toast = useToast();
  const toggle = useM3Mutation((r: RefusalReason) => api.patch(`/refusal-reasons/${r.id}`, { isActive: !r.isActive }));
  return (
    <Panel
      title={title}
      count={reasons.data?.length}
      actions={
        <Button small onClick={() => onDialog({ kind: 'reason', appliesTo: kind })}>
          Добавить
        </Button>
      }
    >
      {reasons.error ? (
        <ErrorState message={reasons.error.message} onRetry={() => void reasons.refetch()} />
      ) : reasons.isLoading ? (
        <Skeleton width="60%" />
      ) : (
        (reasons.data ?? []).map((r) => (
          <div className="lrow rs" key={r.id}>
            <div className="x">
              <div className={r.isActive ? 'n' : 'n dim'}>{r.name}</div>
              {r.isActive ? null : <div className="s">выключена — при выборе не предлагается</div>}
            </div>
            <span className="v dim">
              {r.used}{' '}
              <RowActions
                items={[
                  { label: 'Переименовать…', onClick: () => onDialog({ kind: 'renameReason', reason: r }) },
                  {
                    label: r.isActive ? 'Выключить' : 'Включить',
                    onClick: () =>
                      toggle.mutate(r, {
                        onSuccess: () => toast.show(r.isActive ? 'Причина выключена' : 'Причина включена'),
                        onError: (e) => toast.showError(errorText(e)),
                      }),
                  },
                ]}
              />
            </span>
          </div>
        ))
      )}
      <Note>{note}</Note>
    </Panel>
  );
}

function RenameSourceModal({ source, onClose }: { source: LeadSource; onClose: () => void }) {
  const [name, setName] = useState(source.name);
  const { submit, pending } = useM3Submit(() => api.patch(`/lead-sources/${source.id}`, { name: name.trim() }), 'Канал переименован', onClose);
  return (
    <Modal title={`Название канала «${source.name}»`} onClose={onClose} confirm={{ label: 'Сохранить', onClick: submit, disabled: !name.trim() || pending }}>
      <TextField label="Название" value={name} onValue={setName} />
      <p>
        Новое название появится во всех списках и отчётах — в воронке, карточке заявки, «Привлечении». Код канала{' '}
        <b>{source.code}</b> не меняется: на него завязаны приём заявок и отчёты.
      </p>
    </Modal>
  );
}

function ReissueModal({ source, onClose }: { source: LeadSource; onClose: () => void }) {
  const { submit, pending } = useM3Submit(() => api.post(`/lead-sources/${source.id}/token`), 'Код перевыпущен', onClose);
  return (
    <Modal
      title={`Перевыпустить код «${source.name}»?`}
      onClose={onClose}
      confirm={{ label: 'Перевыпустить', onClick: submit, disabled: pending, danger: true }}
    >
      <p>
        Старый код перестанет работать сразу: формы на сайте и в рекламе, где он стоит, перестанут заводить заявки, пока
        туда не поставят новый.
      </p>
    </Modal>
  );
}

function VkModal({ source, onClose }: { source: LeadSource; onClose: () => void }) {
  const [group, setGroup] = useState(source.vkGroupId ?? '');
  const digits = group.replace(/\D/g, '');
  const { submit, pending } = useM3Submit(
    () => api.patch(`/lead-sources/${source.id}`, { vkGroupId: digits || null }),
    digits ? 'Сообщество подключено' : 'ВКонтакте отключён',
    onClose,
  );
  return (
    <Modal title={`ВКонтакте · канал «${source.name}»`} onClose={onClose} confirm={{ label: 'Сохранить', onClick: submit, disabled: pending }}>
      <p>
        Уведомления сообщества о новых заявках из форм ВКонтакте будут заводить заявки этим каналом. В настройках Callback
        API сообщества секретным ключом ставится код приёма канала.
      </p>
      <TextField label="ID сообщества" inputMode="numeric" placeholder="например, 218770431" value={group} onValue={setGroup} />
    </Modal>
  );
}

function ReasonModal({ appliesTo, reason, onClose }: { appliesTo?: ReasonKind; reason?: RefusalReason; onClose: () => void }) {
  const [name, setName] = useState(reason?.name ?? '');
  const { submit, pending } = useM3Submit(
    () =>
      reason
        ? api.patch(`/refusal-reasons/${reason.id}`, { name: name.trim() })
        : api.post('/refusal-reasons', { name: name.trim(), appliesTo }),
    reason ? 'Причина переименована' : 'Причина добавлена',
    onClose,
  );
  return (
    <Modal
      title={reason ? 'Переименовать причину' : 'Новая причина'}
      onClose={onClose}
      confirm={{ label: reason ? 'Сохранить' : 'Добавить', onClick: submit, disabled: !name.trim() || pending }}
    >
      <TextField label="Формулировка" placeholder="коротко, как в отчёте" value={name} onValue={setName} />
    </Modal>
  );
}
